import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import type { PaginatedPosts } from '~/interfaces/post';

import { useDebouncedCallback } from './useDebouncedCallback';

const POSTS_LOADER_PATH = '/posts';
const DEFAULT_LIMIT = 20;
const DEBOUNCE_MS = 250;

const isPaginatedPosts = (value: unknown): value is PaginatedPosts =>
  typeof value === 'object' && value !== null && Array.isArray((value as { posts?: unknown }).posts);

interface UseCommunityComposerPostsOptions {
  limit?: number;
  enabled?: boolean;
}

const useCommunityComposerPosts = ({
  limit = DEFAULT_LIMIT,
  enabled = true,
}: UseCommunityComposerPostsOptions = {}) => {
  const [query, setQueryState] = useState('');
  const setQuery = useDebouncedCallback(setQueryState, DEBOUNCE_MS);

  const postsQuery = useQuery<PaginatedPosts>({
    queryKey: ['community-composer-posts', query, limit],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams({ per_page: String(limit), page: '1' });
      if (query.trim()) params.set('q', query.trim());
      const res = await fetch(`${POSTS_LOADER_PATH}?${params}`, { signal });
      if (!res.ok) throw new Error('composer_posts_failed');
      const data: unknown = await res.json();
      if (!isPaginatedPosts(data)) throw new Error('composer_posts_invalid');
      return data;
    },
    enabled,
  });

  return { ...postsQuery, setQuery };
};

export default useCommunityComposerPosts;
